import React, { useState } from 'react';
import { MessageCircle, X, Send, Bug, Lightbulb } from 'lucide-react';

const BugsAndIdeas = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'bug' | 'idea'>('idea');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const sendFeedback = async () => {
    if (!message.trim()) return;

    console.log('Sending feedback:', { type, message: message.substring(0, 50) });
    setSending(true);

    try {
      // Send to our API
      const response = await fetch('https://futr-bet-api-hono.b-9f2.workers.dev/feedback/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          message,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        })
      });

      console.log('Feedback API response:', response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Feedback API error:', errorData);
        alert(`Failed to send feedback: ${errorData}`);
        return;
      }

      const data = await response.json();
      console.log('Feedback submitted:', data);

      // Also send to TaskTap for immediate visibility
      try {
        const tasktapResponse = await fetch('https://futr-bet-api-hono.b-9f2.workers.dev/tasktap/broadcast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            task: `${type === 'bug' ? '🐛 BUG' : '💡 IDEA'}: ${message.substring(0, 100)}${message.length > 100 ? '...' : ''} | From: ${window.location.pathname}`,
            reward: type === 'bug' ? 'Bug Fix' : 'Feature Request',
            channel: 'C09H9EC5W7N'
          })
        });
        console.log('TaskTap response:', tasktapResponse.status);
      } catch (tasktapError) {
        console.error('TaskTap broadcast failed:', tasktapError);
        // Don't fail the whole operation if TaskTap fails
      }

      setSent(true);
      setTimeout(() => {
        setMessage('');
        setSent(false);
        setIsOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      alert(`Failed to send feedback: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition-transform z-[9999] text-2xl"
          title="Bugs & Ideas"
        >
          🐞
        </button>
      )}

      {/* Chat Box */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-2xl z-[9999] border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div>
              <h3 className="font-bold text-lg">Bugs & Ideas</h3>
              <p className="text-sm opacity-90">Direct line to Claude & Brian</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-1 rounded"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Type Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setType('bug')}
                className={`flex-1 py-2 px-3 rounded flex items-center justify-center gap-2 transition-colors ${
                  type === 'bug'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                <Bug size={18} />
                <span>Bug Report</span>
              </button>
              <button
                onClick={() => setType('idea')}
                className={`flex-1 py-2 px-3 rounded flex items-center justify-center gap-2 transition-colors ${
                  type === 'idea'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                <Lightbulb size={18} />
                <span>Feature Idea</span>
              </button>
            </div>

            {/* Message Input */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === 'bug'
                  ? "What's broken? Be specific..."
                  : "What's your idea? Dream big..."
              }
              className="w-full h-32 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.metaKey) {
                  sendFeedback();
                }
              }}
            />

            {/* Current Page Info */}
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              From: {window.location.pathname}
            </div>

            {/* Send Button */}
            <button
              onClick={sendFeedback}
              disabled={!message.trim() || sending || sent}
              className={`w-full mt-4 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                sent
                  ? 'bg-green-500 text-white'
                  : sending
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-wait'
                  : message.trim()
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {sent ? (
                <>✓ Sent to the team!</>
              ) : sending ? (
                <>Sending...</>
              ) : (
                <>
                  <Send size={18} />
                  Send to Claude & Brian
                </>
              )}
            </button>

            {/* Shortcut Hint */}
            <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
              Tip: ⌘+Enter to send
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BugsAndIdeas;